import type { ReactNode } from 'react'

import { t } from '../../../common/i18n.js'
import { View } from '../../types.js'
import { loadMyProfileIfNeeded, reloadPlaytimeProfile } from '../../utils/load-utils.js'
import { getStatusLabel } from '../../views/portal/utils/format.js'
import { saveMyAvatar, uploadAvatarImage } from '../../views/portal/utils/portal-api.js'
import { PageShell } from '../page-shell/page-shell.js'
import { ProfileBanner } from '../profile-banner/profile-banner.js'
import { SubNav } from '../sub-nav/sub-nav.js'
import type { SubNavItem } from '../sub-nav/sub-nav.js'

export type MeNavKey =
    | 'data'
    | 'absences'
    | 'tasks'
    | 'worktime'
    | 'projects'
    | 'assets'
    | 'documents'
    | 'settings'

export interface MeLayoutProps {
    view: View
    active: MeNavKey
    children: ReactNode
    actions?: ReactNode
}

export const MeLayout = ({ view, active, children, actions }: MeLayoutProps) => {
    const lang = view.state.lang
    const meState = loadMyProfileIfNeeded(view)
    const me = meState.data
    const profile = t.portal.profile
    const nav = t.portal.nav

    const items: SubNavItem[] = [
        { key: 'absences', label: profile.timeAwayTab(lang), href: '/me/time-away' },
        { key: 'tasks', label: nav.tasks(lang), href: '/me/tasks' },
        { key: 'worktime', label: nav.worktime(lang), href: '/me/balancetime' },
        { key: 'projects', label: profile.projectsTab(lang), href: '/me/worktime' },
        { key: 'assets', label: nav.myAssets(lang), href: '/me/assets' },
        { key: 'documents', label: t.portal.documents.title(lang), href: '/me/documents' },
        { key: 'data', label: profile.dataTab(lang), href: '/me' },
        { key: 'settings', label: profile.settingsTab(lang), href: '/me/settings' },
    ]

    const name = me?.displayName ?? view.state.user?.name ?? t.common.userFallback(lang)
    const status = me?.status ?? undefined
    const statusLabel = status ? getStatusLabel(status, lang) : undefined

    const refreshMe = () => {
        view.state.me.data = undefined
        view.state.portalRole.data = undefined
        reloadPlaytimeProfile(view)
        view.update()
    }

    const handleAvatarChange = async (file: File): Promise<boolean> => {
        const uploadRes = await uploadAvatarImage(file)
        if (!uploadRes.ok) return false
        const saveRes = await saveMyAvatar(uploadRes.data.imageUrl)
        if (!saveRes.ok) return false
        refreshMe()
        return true
    }

    const handleAvatarRemove = async (): Promise<boolean> => {
        const res = await saveMyAvatar(null)
        if (!res.ok) return false
        refreshMe()
        return true
    }

    return (
        <PageShell>
            <ProfileBanner
                name={name}
                avatarUrl={me?.avatarUrl}
                jobTitle={me?.jobTitle}
                email={me?.workEmail ?? me?.email}
                companies={me?.companies}
                status={status}
                statusLabel={statusLabel}
                tenure={me?.tenure}
                lang={lang}
                onAvatarChange={handleAvatarChange}
                onAvatarRemove={handleAvatarRemove}
            />
            <SubNav items={items} activeKey={active} actions={actions} sticky lang={lang} />
            {children}
        </PageShell>
    )
}
